import QuotasConfirmed from './QuotasConfirmed';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const MonthlyDetails = ({ response }) => {

  // Función para determinar qué vista mostrar
  const renderView = () => {
    
    // Caso 1: Cuotas confirmadas - ahora response es directamente el array
    if (response && Array.isArray(response) && response.length > 0) {
      // Calcular valor UF usado (tomamos el primer elemento como referencia)
      const ufUsed = response[0]?.uf_value || '0.00';
      return <QuotasConfirmed quotas={response} ufUsed={ufUsed} onExportExcel={generateExcel} />;
    } else if (response && Array.isArray(response) && response.length === 0) {
      // Array vacío - no hay cuotas para el período
      return returnNoQuotasFound();
    } else {
      return returnWarningError();
    }
  };


  const generateExcel = async () => {
    if (!response || response.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Cuotas Confirmadas');

    // Definir columnas
    sheet.columns = [
      { header: 'Nombre Empresa', key: 'empresa' },
      { header: 'RUT Empresa', key: 'rut' },
      { header: 'Monto', key: 'monto' },
      { header: 'Estado', key: 'estado' },
      { header: 'Fecha de Pago', key: 'fecha_pago' },
      { header: 'Medio de Pago', key: 'medio_pago' },
      { header: 'Número Documento', key: 'documento' },
    ];

    // Función para obtener el medio de pago clasificado
    const getPaymentMethod = (quota) => {
      // Si la cuota no está pagada, retornar sin pago
      if (quota.status !== 'paid') {
        return 'Sin pago';
      }
      
      // Verificar que paymentSocialQuotas exista y tenga elementos
      if (!quota.paymentSocialQuotas || !Array.isArray(quota.paymentSocialQuotas) || quota.paymentSocialQuotas.length === 0) {
        return 'Sin pago';
      }
      
      // Buscar el método de pago en todos los paymentSocialQuotas
      let method = null;
      for (const paymentSocialQuota of quota.paymentSocialQuotas) {
        if (paymentSocialQuota?.payment?.payment_method) {
          method = paymentSocialQuota.payment.payment_method;
          break; // Usar el primer método de pago encontrado
        }
      }
      
      // Si no se encontró método de pago
      if (!method) {
        return 'Sin pago';
      }
      
      const methodLower = String(method).trim().toLowerCase();
      
      // Si es transferencia
      if (methodLower === 'transferencia') {
        return 'Transferencia';
      }
      
      // Códigos de Transbank (todos los tipos de pago de Transbank)
      const transbankCodes = ['vn', 's2', 'si', 'nc', 'vc', 'vd', 'vp', 'webpay']; // VN: Venta Normal, S2: 2 Cuotas sin interés, SI: 3 Cuotas sin interés, NC: N Cuotas sin interés, VC: Cuotas normales, VD: Venta débito Redcompra, VP: Venta Prepago
      if (transbankCodes.includes(methodLower)) {
        return 'Transbank';
      }
      
      // Por defecto, si está pagado pero no reconocemos el método
      return 'Sin pago';
    };

    // Insertar datos
    response.forEach(quota => {
      sheet.addRow({
        empresa: quota.company.trade_name || quota.company.business_name,
        rut: quota.company.rut,
        monto: parseFloat(quota.amount || 0),
        estado: quota.status === 'pending' ? 'Pendiente' : quota.status === 'paid' ? 'Pagado' : 'Otro',
        fecha_pago: quota.payment_date 
          ? new Date(quota.payment_date).toLocaleDateString('es-CL') 
          : 'Sin pago',
        medio_pago: getPaymentMethod(quota),
        documento: quota.id,
      });
    });

    const lastDataRow = sheet.lastRow.number;

    // Estilo cabeceras
    sheet.getRow(1).height = 25;
    sheet.getRow(1).eachCell(cell => {
      cell.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0927A4' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
    });

    // Ajuste automático ancho
    sheet.columns.forEach(col => {
      let maxLength = col.header.length;
      col.eachCell?.(cell => {
        const valueLength = cell.value ? cell.value.toString().length : 0;
        if (valueLength > maxLength) maxLength = valueLength;
      });
      col.width = maxLength + 7;
    });

    // Estilo filas de datos
    for (let i = 2; i <= lastDataRow; i++) {
      const fillColor = i % 2 === 0 ? 'E3F0FA' : 'D6EAFB';
      sheet.getRow(i).height = 22;
      sheet.getRow(i).eachCell(cell => {
        cell.font = { size: 12 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
        cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
      });
    }

    sheet.getColumn('monto').numFmt = '"$"#,##0';

    // Cálculo Totales
    const totalGeneral = response.reduce((acc, q) => acc + parseFloat(q.amount || 0), 0);
    const totalPagado = response.reduce((acc, q) =>
      q.status === 'paid' ? acc + parseFloat(q.amount || 0) : acc, 0);

    const totalRow1 = lastDataRow + 1;
    const totalRow2 = lastDataRow + 2;

    // TOTAL GENERAL
    sheet.mergeCells(`A${totalRow1}:B${totalRow1}`);
    sheet.getCell(`A${totalRow1}`).value = 'TOTAL GENERAL:';
    sheet.getCell(`C${totalRow1}`).value = totalGeneral;
    sheet.getCell(`C${totalRow1}`).numFmt = '"$"#,##0';

    ['A', 'B', 'C'].forEach(col => {
      const cell = sheet.getCell(`${col}${totalRow1}`);
      cell.font = { bold: true, size: 13, color: { argb: '0927A4' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E3F0FA' } };
      cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
    });

    // TOTAL PAGADO
    sheet.mergeCells(`A${totalRow2}:B${totalRow2}`);
    sheet.getCell(`A${totalRow2}`).value = 'TOTAL PAGADO:';
    sheet.getCell(`C${totalRow2}`).value = totalPagado;
    sheet.getCell(`C${totalRow2}`).numFmt = '"$"#,##0';

    ['A', 'B', 'C'].forEach(col => {
      const cell = sheet.getCell(`${col}${totalRow2}`);
      cell.font = { bold: true, size: 13, color: { argb: '0488AF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D6EAFB' } };
      cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
    });

    // Generar y Descargar
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const period = response[0]?.period || 'periodo';
    const year = response[0]?.year || new Date().getFullYear();
    saveAs(blob, `Cuotas_Confirmadas_${period}_${year}.xlsx`);
  };



  const returnNoQuotasFound = () => {
    return (
      <div className="p-4 border border-blue-300 bg-blue-50 text-blue-800 rounded">
        <p className="font-semibold">No se encontraron cuotas para este período:</p>
        <p>No hay cuotas registradas para el período seleccionado.</p>
      </div>
    );
  };

  const returnWarningError = () => {
    return (
      <div className="p-4 border border-yellow-300 bg-yellow-50 text-yellow-800 rounded">
        <p className="font-semibold">Las cuotas no han sido enviadas:</p>
        <p>Recuerda que debes enviar las cuotas antes de poder ver el reporte mensual</p>
        <p>Realiza el envío accediendo a "Envío de cuotas" en la sección anterior</p>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Detalle Mensual - Cuotas Sociales
        </h2>
        <p className="text-gray-600">
          Gestión y visualización de cuotas sociales mensuales
        </p>
      </div>
      
      {renderView()}
    </div>
  );
};

export default MonthlyDetails;